import { zodResolver } from '@hookform/resolvers/zod';
import { Paper } from '@mui/material';
import { isNotEmptyString, isNotNullOrUndefined, withDefault } from '@weft/utils';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { withSuspenseBoundary } from '../../../../../components/utility/SuspenseBoundary';
import { usePlansOverviewsWithPageParams } from '../../../../../hooks/requests/use-plans-overview/use-plans-overviews';
import { usePaginationResetHandler } from '../../../../../hooks/use-pagination-reset-handler';
import type { NewPlanAssignment } from '../../plans-page-types/new-plan-assignment-schema';
import { NewPlanAssignmentSchema } from '../../plans-page-types/new-plan-assignment-schema';
import type { PlanAssignmentInput } from '../../plans-page-types/plan-assignment-input-schema';
import { CreateContractSidebar } from '../CreateContractSidebar';
import { PlansOverviewTable } from '../PlansOverviewTable';
import { PlansOverviewsTableToolbar } from './plans-tab-content-components/PlansOverviewsTableToolbar';

const PlansTabContent: React.FC = () => {
  const { data: plansOverviewsResponse, isFetching: isFetchingPlansOverviews } = useTablePlansOverviews();
  usePaginationResetHandler(plansOverviewsResponse?.plansOverviews.length ?? 0);

  const methods = useForm<PlanAssignmentInput, NewPlanAssignment>({
    resolver: zodResolver(NewPlanAssignmentSchema),
  });

  const contract = methods.watch('contract');

  return (
    <FormProvider {...methods}>
      <Paper
        sx={{
          height: '100%',
          overflow: 'hidden',
          borderRadius: '10px',
        }}
        component="form"
      >
        <PlansOverviewsTableToolbar/>

        <PlansOverviewTable
          items={withDefault(plansOverviewsResponse?.plansOverviews, [])}
          itemsCount={withDefault(plansOverviewsResponse?.countedFilteredPlansOverviews, 0)}
          loadingItems={isFetchingPlansOverviews}
        />

        {isNotNullOrUndefined(contract) && isNotEmptyString(contract.planId) && (
          <CreateContractSidebar
            title="Assign customer"
            planId={contract.planId}
          />
        )}

      </Paper>
    </FormProvider>
  );
};

const PlansTabContentWithSuspenseBoundary = withSuspenseBoundary(PlansTabContent);
export { PlansTabContentWithSuspenseBoundary as PlansTabContent };

function useTablePlansOverviews() {
  const { data: plansOverviewsResponse, ...restPlansOverviewsResponse } = usePlansOverviewsWithPageParams();

  return {
    ...restPlansOverviewsResponse,
    data: plansOverviewsResponse,
  };
}